import { Box, Button, ClickAwayListener, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { useBuilding } from "../../provider/BuildingProvider/BuildingProvider";

import { useMultiSelectToggleContext } from "../../provider/MultiSelectToggleProvider";
import {
    COMPASS_IMAGE_PATH,
    FULL_CIRCLE_DEGREES,
    HALF,
    HALF_CIRCLE_DEGREES
} from "../../utils/constants";

// Static styles
const styles = {
    container: {
        padding: ".25rem",
        backgroundColor: "#aaaaaa40",
        zIndex: 999,
        right: 0,
        position: "absolute",
        top: "65px",
        transform: "translate(-10%, 10%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        userSelect: "none",
    },
    image: {
        width: "6rem",
        height: "6rem",
        cursor: "grab",
    },
};

// Get angle from [0, 0] to a point
const getAngle = (point: THREE.Vector2): number => {
    const vec = new THREE.Vector2(point.x, point.y);
    return (vec.angle() * HALF_CIRCLE_DEGREES) / Math.PI;
};

// Normalize angle (in degrees) to range [0, 360]
const normalizeAngle = (angle: number): number => {
    angle = angle % FULL_CIRCLE_DEGREES;
    if (angle < 0) angle += FULL_CIRCLE_DEGREES;
    return angle;
};

const CompassWidget = () => {
    const { showGeneralInfo } = useMultiSelectToggleContext();
    const { building } = useBuilding();

    const [rotation, setRotation] = useState<number>(
        building.compass.getRotation()
    ); // Synchronize with building.compass
    const [startAngle, setStartAngle] = useState<number | undefined>();
    const [pivot, setPivot] = useState<THREE.Vector2>();
    const [inputMode, _setInputMode] = useState<boolean>(false);

    const imgRef = useRef<HTMLImageElement>(null);
    const textFieldRef = useRef<HTMLInputElement>(null);

    // Ensure rotation stays synced with building.compass
    useEffect(() => {
        const currentRotation = building.compass.getRotation();
        if (rotation !== currentRotation) {
            setRotation(currentRotation);
        }
    }, [building.compass, rotation]);

    // Update building.compass rotation
    const updateRotation = (newRotation: number) => {
        const normalized = building.compass.normalizeAngle(newRotation);
        building.compass.setRotation(normalized);
        setRotation(normalized); // Sync local state
    };

    // Update pivot point
    const updatePivot = () => {
        if (!imgRef.current) return;

        const rect = imgRef.current.getBoundingClientRect();
        setPivot(
            new THREE.Vector2(rect.x + rect.width * HALF, rect.y + rect.height * HALF)
        );
    };

    useEffect(() => {
        updatePivot();
    }, []);

    // Handle mouse move events
    const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>): void => {
        if (e.buttons !== 1 || !pivot) {
            setStartAngle(undefined);
            return;
        }

        const angle = normalizeAngle(
            getAngle(new THREE.Vector2(e.clientX - pivot.x, e.clientY - pivot.y))
        );

        if (startAngle === undefined) {
            setStartAngle(angle);
        } else {
            const newRot = angle - startAngle;
            updateRotation(newRot);
        }
    };

    // Handle manual rotation input
    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (!inputMode) return;

        const newRot = Number(e.target.value);
        if (!isNaN(newRot)) {
            updateRotation(newRot);
        } else {
            updateRotation(0);
        }
    };

    const setInputMode = (newInputMode: boolean) => {
        _setInputMode(newInputMode);
        if (newInputMode && textFieldRef.current) {
            textFieldRef.current.focus();
        }
    };

    const handleSubmitInput = () => {
        setInputMode(false);
    };

    const handleKeyUp = (e: React.KeyboardEvent) => {
        if (e.code === "Enter") handleSubmitInput();
    };

    if (!showGeneralInfo) return null;

    return (
        <Box component="div" sx={styles.container}>
            <img
                src={COMPASS_IMAGE_PATH}
                style={{ ...styles.image, transform: `rotate(${rotation}deg)` }}
                ref={imgRef}
                draggable={false}
                onMouseMove={handleMouseMove}
                onMouseDown={updatePivot}
                className="compass-widget-img"
            />

            <ClickAwayListener onClickAway={handleSubmitInput}>
                {inputMode ? (
                    <TextField
                        size="small"
                        sx={{ width: "5rem" }}
                        type="number"
                        value={rotation.toFixed(0)} 
                        onChange={handleInput}
                        onKeyUp={handleKeyUp}
                        ref={textFieldRef}
                        inputRef={(ref) => ref && ref.focus()}
                    />
                ) : (
                    <Button
                        variant="outlined"
                        onClick={() => setInputMode(true)}
                        sx={{
                            backgroundColor: "transparent",
                            border: "none",
                            color: "black",
                        }}
                    >
                        {rotation.toFixed(0) + "°"}
                    </Button>
                )}
            </ClickAwayListener>
        </Box>
    );
};

export default CompassWidget;
